import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  TeButtonOutline,
  TeContainer,
  TeContainerSlim,
  TeIcon,
  TeInputText,
  TeLabel,
  TeText,
} from '@topcon/tuicl-react';

import { background, border } from '../../../providers/theme/theme.index';
import { BusinessPartnerBody } from '../../../api/generated/pm';
import IconWrapper from '../../IconWrapper';
import { AvailableAgreementsFilters } from '../../../pages/Settings/Dealers/dealers.queries';
import { CountryDropdown } from '../../CountryDropdown';
import CompanyIcon from '../../Company/CompanyIcon';

const DEALER_CARD_HEIGHT = 82;
const FILTERS_CONTAINER_MIN_WIDTH = 256;
const PROVIDERS_CONTAINER_MIN_WIDTH = 512;

interface DealerSelectorProps {
  filters: AvailableAgreementsFilters;
  onFiltersChange: (update: Partial<AvailableAgreementsFilters>) => void;
  providers: BusinessPartnerBody[] | undefined;
  // productLines: ProductLineBody[] | undefined;
  loading: boolean;
  onSelect: (bp: BusinessPartnerBody) => void;
  selectedDealer: BusinessPartnerBody | undefined;
}

function DealerSelector({
  filters,
  onFiltersChange,
  providers,
  loading,
  onSelect,
  selectedDealer,
}: DealerSelectorProps) {
  const { t } = useTranslation();

  return (
    <StyledRootContainer data-testid="modal-step-3">
      <StyledFiltersContainer grow>
        <TeContainerSlim column fill gap="xs" padding="5px">
          <TeLabel text={t('dealers.createModal.name')} />
          <TeInputText
            grow
            placeholder={t('dealers.createModal.namePlaceholder')}
            name="name"
            size="s"
            value={filters.name || ''}
            techange={(e) => onFiltersChange({ name: e.target.value })}
          />
        </TeContainerSlim>

        <TeContainerSlim column fill gap="xs" padding="5px">
          <TeLabel text={t('dealers.createModal.country')} />
          <CountryDropdown
            selectedCountry={filters.country ?? ''}
            onSelectCountry={(country) => onFiltersChange({ country: country?.code })}
            dropdownPosition="down"
            required={false}
            clearable={true}
          />
        </TeContainerSlim>
      </StyledFiltersContainer>

      <StyledProvidersContainer>
        {loading ? (
          <StyledResultState>
            <TeText>{t('dealers.states.loading')}</TeText>
          </StyledResultState>
        ) : !providers || providers.length === 0 ? (
          <StyledResultState>
            <TeText>{t('dealers.states.noResults')}</TeText>
          </StyledResultState>
        ) : (
          <StyledResultList>
            {providers.map((dealer) => (
              <DealerCard
                dealer={dealer}
                key={dealer.id}
                onSelect={onSelect}
                selected={selectedDealer?.id === dealer.id}
                data-testid={`dealer-card-${dealer.id}`}
              />
            ))}
          </StyledResultList>
        )}
      </StyledProvidersContainer>
    </StyledRootContainer>
  );
}

function DealerCard({
  dealer,
  onSelect,
  selected,
}: {
  dealer: BusinessPartnerBody;
  onSelect: (bp: BusinessPartnerBody) => void;
  selected?: boolean;
}) {
  const address = `${dealer?.companyAddress.addressLineOne}, ${dealer?.companyAddress.zipPostalCode} ${dealer?.companyAddress.city} ${dealer?.companyAddress.stateProvinceRegion}, ${dealer?.companyAddress.country}`;

  return (
    <TeContainerSlim
      fill
      grow
      borderRadius="l"
      backgroundColor={selected ? 'background.selected' : 'background.secondary'}
      padding="s"
      height={DEALER_CARD_HEIGHT}
      justifyContent="space-between"
      alignItems="center"
      gap={'xs'}
      borderColor={selected ? 'border.brand.primary' : 'border.primary'}
    >
      <TeContainerSlim fill grow alignItems="center" gap="xs">
        <CompanyIcon size="l" />
        <TeContainerSlim fill grow column>
          <TeText weight={700} size={14}>
            {dealer?.name}
          </TeText>
          <TeText weight={400} size={12}>
            {address}
          </TeText>
        </TeContainerSlim>
      </TeContainerSlim>
      {selected ? (
        <IconWrapper backgroundColor={background.brand.primary} size={36}>
          <TeIcon size="m" name="check" color="icon.inverse" />
        </IconWrapper>
      ) : (
        <TeButtonOutline text="Select" size="s" teclick={() => onSelect(dealer)} />
      )}
    </TeContainerSlim>
  );
}

const StyledRootContainer = styled(TeContainer)`
  background-color: ${background.primary} !important;
  flex-grow: 1;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  padding: 0 !important;
  width: 100% !important;
`;

const StyledFiltersContainer = styled(TeContainer)`
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${border.secondary} !important;
  min-width: ${FILTERS_CONTAINER_MIN_WIDTH}px !important;
`;

const StyledProvidersContainer = styled(TeContainer)`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-width: ${PROVIDERS_CONTAINER_MIN_WIDTH}px !important;
`;

const StyledResultState = styled(`div`)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const StyledResultList = styled(`div`)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

export default DealerSelector;
